import React, { useEffect, useId, useRef } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Diálogo de confirmación controlado.
 * Para acciones destructivas usa `peligro`: el botón de confirmar va en rojo y el foco
 * inicial queda en "Cancelar". El mensaje debe nombrar lo que se afecta y su consecuencia.
 *
 * @param {object} props
 * @param {boolean} props.abierto
 * @param {React.ReactNode} props.titulo
 * @param {React.ReactNode} [props.mensaje]
 * @param {string} [props.textoConfirmar] Por defecto "Confirmar".
 * @param {string} [props.textoCancelar] Por defecto "Cancelar".
 * @param {boolean} [props.peligro] Acción destructiva.
 * @param {boolean} [props.cargando] Deshabilita los botones y muestra el progreso en el de confirmar.
 * @param {Function} props.onConfirmar
 * @param {Function} props.onCancelar Se llama al cancelar, cerrar o pulsar Escape.
 */
export const ConfirmDialog = ({
  abierto,
  titulo,
  mensaje,
  textoConfirmar = "Confirmar",
  textoCancelar = "Cancelar",
  peligro = false,
  cargando = false,
  onConfirmar,
  onCancelar,
}) => {
  const idTitulo = useId();
  const idMensaje = useId();
  const reducirMovimiento = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Protección contra el doble clic en Confirmar: tras el primer clic se ignoran los siguientes,
  // aunque el padre aún no haya puesto "cargando" (p. ej. lo pone después de un await).
  // Se libera cuando el diálogo se vuelve a abrir, o cuando termina el "cargando" con el
  // diálogo aún abierto (la operación falló y el usuario puede reintentar).
  const confirmado = useRef(false);
  const cargandoAntes = useRef(cargando);
  useEffect(() => {
    if (abierto) confirmado.current = false;
  }, [abierto]);
  useEffect(() => {
    if (cargandoAntes.current && !cargando) confirmado.current = false;
    cargandoAntes.current = cargando;
  }, [cargando]);

  const confirmar = (evento) => {
    if (!abierto || cargando || confirmado.current) return;
    confirmado.current = true;
    if (onConfirmar) onConfirmar(evento);
  };

  // Mientras se procesa no se puede cerrar: la petición ya salió y cerrar daría la idea de que se canceló.
  const cerrar = () => {
    if (!cargando && onCancelar) onCancelar();
  };

  return (
    <Dialog
      open={Boolean(abierto)}
      onClose={cerrar}
      maxWidth="xs"
      fullWidth
      aria-labelledby={idTitulo}
      aria-describedby={mensaje ? idMensaje : undefined}
      transitionDuration={reducirMovimiento ? 0 : undefined}
    >
      <DialogTitle id={idTitulo} sx={{ fontWeight: 700, pr: 7 }}>
        {titulo}
      </DialogTitle>
      <IconButton
        aria-label="Cerrar"
        onClick={cerrar}
        disabled={cargando}
        sx={{
          position: "absolute",
          top: (theme) => theme.spacing(1.5),
          right: (theme) => theme.spacing(1.5),
          color: "text.secondary",
        }}
      >
        <CloseIcon />
      </IconButton>

      {mensaje && (
        <DialogContent>
          <DialogContentText
            component="div"
            id={idMensaje}
            sx={{ color: "text.primary" }}
          >
            {mensaje}
          </DialogContentText>
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={cerrar} disabled={cargando} autoFocus={peligro}>
          {textoCancelar}
        </Button>
        <Button
          variant="contained"
          color={peligro ? "error" : "primary"}
          onClick={confirmar}
          disabled={cargando}
          autoFocus={!peligro}
          startIcon={
            cargando ? (
              // Con "reducir movimiento" el progreso queda quieto (determinado, valor fijo).
              <CircularProgress
                size={16}
                color="inherit"
                variant={reducirMovimiento ? "determinate" : "indeterminate"}
                value={25}
              />
            ) : undefined
          }
        >
          {textoConfirmar}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
